import React from 'react';
import { Shield, Zap, Lock, Settings, TrendingUp, UserCheck, Clock, Cpu } from 'lucide-react';
import { Button } from '../ui/button';
import { securityFeatures, quickTestimonials } from '../../data/mockData';

const SecuritySection = ({ onBookDemo }) => {
  const icons = [Shield, Zap, Lock, Settings];
  
  const iconMap = {
    'trending-up': TrendingUp,
    'user-check': UserCheck,
    'clock': Clock,
    'zap': Zap,
    'cpu': Cpu
  };

  return (
    <section className="bg-white py-24" id="about" aria-labelledby="security-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div>
            <span className="text-sm font-medium text-purple-600 mb-3 block">Security</span>
            <h2 id="security-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Enable every team to build AI Agents,{' '}
              <span className="text-gray-400">securely</span>
            </h2>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              Product teams securely build and deploy advanced AI agents using their approved LLMs alongside customer data and tech stack integrations.
            </p>
            <Button
              onClick={onBookDemo}
              className="bg-gray-900 text-white hover:bg-gray-800 font-medium px-6"
              aria-label="Book a demo to learn about security features"
            >
              Book a demo
            </Button>
          </div>

          {/* Right - Features Grid */}
          <div className="grid sm:grid-cols-2 gap-4" role="list" aria-label="Security and platform features">
            {securityFeatures.map((feature, index) => {
              const Icon = icons[index];
              return (
                <article
                  key={feature.id}
                  role="listitem"
                  className="p-6 rounded-xl bg-gray-50 border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center mb-4" aria-hidden="true">
                    <Icon className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-gray-900 font-medium mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>

        {/* Trusted By */}
        <aside className="mt-20" aria-label="Trust indicators">
          <p className="text-gray-400 text-sm mb-8 text-center">Trusted by the best in the business</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-5xl mx-auto" role="list" aria-label="Customer success metrics">
            {quickTestimonials.map((item) => {
              const Icon = iconMap[item.icon] || Zap;
              return (
                <div
                  key={item.id}
                  role="listitem"
                  className="flex flex-col items-center p-4 rounded-xl bg-gray-50 border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center mb-3" aria-hidden="true">
                    <Icon className="w-5 h-5 text-purple-600" />
                  </div>
                  <p className="text-xs text-gray-600 text-center font-medium leading-tight">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </aside>
      </div>
    </section>
  );
};

export default SecuritySection;
